import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Input, Button, Row, Col, message, Upload, Select } from 'antd';
import { 
  UserOutlined, 
  IdcardOutlined, 
  PhoneOutlined, 
  HomeOutlined, 
  BankOutlined, 
  TeamOutlined,
  LoadingOutlined,
  PlusOutlined
} from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';

const { Option } = Select;

const EditStudentModal = ({ isOpen, onClose, onSave, studentId }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [, setLoading] = useState(true);
    const [, setFileList] = useState([]);    
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [uploadLoading, setUploadLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const API_URL = process.env.REACT_APP_API_URL;

    const educationOptions = [
  { value: 'Grade 1', label: t('education_levels.grade1') },
  { value: 'Grade 2', label: t('education_levels.grade2') },
  { value: 'Grade 3', label: t('education_levels.grade3') },
  { value: 'Grade 4', label: t('education_levels.grade4') },
  { value: 'Grade 5', label: t('education_levels.grade5') },
  { value: 'Grade 6', label: t('education_levels.grade6') },
  { value: 'Grade 7', label: t('education_levels.grade7') },
  { value: 'Grade 8', label: t('education_levels.grade8') },
  { value: 'Grade 9', label: t('education_levels.grade9') },
  { value: 'Grade 10', label: t('education_levels.grade10') },
  { value: 'Grade 11', label: t('education_levels.grade11') },
  { value: 'Grade 12', label: t('education_levels.grade12') },
  { value: 'University Year 1', label: t('education_levels.uni1') },
  { value: 'University Year 2', label: t('education_levels.uni2') },
  { value: 'University Year 3', label: t('education_levels.uni3') },
  { value: 'University Year 4', label: t('education_levels.uni4') },
  { value: 'Adult', label: t('education_levels.adult') }
];

    useEffect(() => {
    if (!isOpen) {
        form.resetFields();
        setImageUrl('');
        setPreviewImage(''); // Reset preview
        setDateOfBirth('');
        setFileList([]);
        setLoading(true);
    }
}, [isOpen, form]);

    useEffect(() => {
        // Only fetch if the modal is open and we have a student ID
        if (isOpen && studentId) {
            const fetchStudent = async () => {
                // Reset image URL at the start of fetch to prevent showing old image
                setImageUrl('');
                
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(`${API_URL}/students/${studentId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
    
                    const studentData = response.data;
    
                    // Format date of birth properly for the input field
                    let formattedDateOfBirth = '';
                    if (studentData.date_of_birth) {
                        // Convert to YYYY-MM-DD format
                        const date = new Date(studentData.date_of_birth);
                        formattedDateOfBirth = date.toISOString().split('T')[0];
                        setDateOfBirth(formattedDateOfBirth);
                    }
                    
                    // Set all form fields except date of birth which is handled separately
                    form.setFieldsValue({
                        ...studentData,
                        date_of_birth: undefined // Clear this to avoid conflict with our custom handler
                    });
    
                    if (studentData.nationalIdPdf) {
                        setFileList([
                            {
                                uid: '-1',
                                name: 'Uploaded File',
                                status: 'done',
                                url: studentData.nationalIdPdf,
                            }
                        ]);
                    }
    
                    // Set profile photo if available
                   // Set profile photo if available
                if (studentData.profile_photo) {
                    // Extract base URL (remove the API path part)
                    const baseUrl = API_URL.replace(/\/api.*$/, '');
                    
                    // Handle the case where profile_photo contains /var/www/uploads
                    if (studentData.profile_photo.includes('/var/www/uploads')) {
                        // Replace /var/www with nothing to get the correct URL path
                        const fixedPath = studentData.profile_photo.replace('/var/www', '');
                        setImageUrl(`${baseUrl}${fixedPath}`);
                    } else if (studentData.profile_photo.startsWith('/uploads')) {
                        // Already a relative path
                        setImageUrl(`${baseUrl}${studentData.profile_photo}`);
                    } else if (studentData.profile_photo.startsWith('http')) {
                        // If it's already a full URL, use it directly
                        setImageUrl(studentData.profile_photo);
                    } else {
                        // For any other format, append to base URL
                        setImageUrl(`${baseUrl}/${studentData.profile_photo}`);
                    }

                }
                } catch (error) {
                    console.error('Error fetching student:', error);
                    message.error(t('fetchStudentError'));
                } finally {
                    setLoading(false);
                }
            };
    
            fetchStudent();
        }
    }, [studentId, API_URL, form, t, isOpen]);

    
const beforeUpload = async (file) => {
  const isValidType = file.type === 'image/jpeg' || file.type === 'image/png';
  const isValidSize = file.size / 1024 / 1024 < 2;

  if (!isValidType) {
    message.error('You can only upload JPG/PNG file!');
    return false;
  }

  if (!isValidSize) {
    message.error('Image must be smaller than 2MB!');
    return false;
  }

  // Preview image
  const reader = new FileReader();
  reader.onload = (e) => {
    setPreviewImage(e.target.result);
  };
  reader.readAsDataURL(file);

  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('profilePhoto', file);
    formData.append('studentId', studentId); 

    const res = await axios.post(`${API_URL}/image-upload/profile-photo`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    if (res.data?.url) {
      form.setFieldsValue({ profile_photo_url: res.data.url });
      message.success('Uploaded successfully');
    } else {
      throw new Error('No URL returned from server');
    }
  } catch (err) {
    console.error('Upload error:', err);
    message.error('Failed to upload image');
  }

  return false;
};

const handleProfilePhotoChange = ({ file }) => {
  if (file.status === 'uploading') {
    setUploadLoading(true);
    return;
  }

  if (file.status === 'done' || file.status === 'error') {
    setUploadLoading(false);
  }

  if (file.originFileObj) {
    form.setFieldsValue({
      profilePhoto: {
        fileList: [file]
      }
    });
  }
};

const handleSubmit = async (values) => {
  try {
    const token = localStorage.getItem('token');

    const payload = {
      ...values,
      date_of_birth: dateOfBirth,
      schoolid: values.schoolid ? parseInt(values.schoolid) : '',
      profile_photo_url: values.profile_photo_url || null
    };

    const res = await axios.put(`${API_URL}/students/${studentId}`, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });

    message.success(t('studentUpdatedSuccessfully'));

    if (onSave) onSave(res.data);
    onClose();
  } catch (err) {
    console.error('Submit error:', err);
    message.error(t('errorUpdatingStudent'));
  }
};

    // Custom upload button for profile photo
    const uploadButton = (
        <div>
            {uploadLoading ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 8 }}>{t('upload')}</div>
        </div>
    );
    
    return (
        <Modal
            title={t('editStudent')}
            open={isOpen}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>{t('cancel')}</Button>,
                <Button key="submit" type="primary" onClick={() => form.submit()}>{t('save')}</Button>,
            ]}
            width={800}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{}}
            >
                {/* Profile Photo Upload Section - At the top */}
                <Row justify="center" style={{ marginBottom: 24 }}>
                    <Col>
                        <div style={{ textAlign: 'center' }}>
                            <Form.Item 
                                name="profilePhoto" 
                                valuePropName="file"
                                noStyle
                            >
                                <Upload
                                    name="profilePhoto"
                                    listType="picture-card"
                                    className="avatar-uploader"
                                    showUploadList={false}
                                    beforeUpload={beforeUpload}
                                    onChange={handleProfilePhotoChange}
                                    maxCount={1}
                                    accept=".jpg,.jpeg,.png"
                                >
                                    {(previewImage || imageUrl) ? (
                                        <div style={{ position: 'relative', width: '100px', height: '100px' }}>
                                            <img 
                                                src={previewImage || imageUrl} 
                                                alt="Profile" 
                                                style={{ 
                                                    width: '100%', 
                                                    height: '100%', 
                                                    objectFit: 'cover',
                                                    borderRadius: '8px'
                                                }} 
                                            />
                                            {previewImage && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '5px',
                                                    right: '5px',
                                                    background: 'rgba(0,0,0,0.6)',
                                                    color: 'white',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    fontSize: '10px'
                                                }}>
                                                    New
                                                </div>
                                            )}
                                        </div>
                                    ) : uploadButton}
                                </Upload>
                            </Form.Item>
                            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                {t('Click to upload a new photo') || 'Click to upload profile photo'}
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Form Fields */}
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="firstname"
                            label={t('firstName')}
                            rules={[{ required: true, message: t('form.required_field') }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder={t('firstName')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="lastname"
                            label={t('lastName')}
                            rules={[{ required: true, message: t('form.required_field') }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder={t('lastName')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="nickname"
                            label={t('nickname')}
                        >
                            <Input placeholder={t('nickname')} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="gender"
                            label={t('gender')}
                            rules={[{ required: false }]}
                        >
                            <Select placeholder={t('selectGender')}>
                                <Option value="male">{t('male')}</Option>
                                <Option value="female">{t('female')}</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="email"
                            label={t('email')}
                            rules={[{ required: true, type: 'email', message: t('form.invalid_email') }]}
                        >
                            <Input placeholder={t('email')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label={t('dateOfBirth')}
                            required
                        >
                            <input
                                type="date"
                                value={dateOfBirth}  
                                onChange={(e) => setDateOfBirth(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #d9d9d9",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="nationalid"
                            label={t('nationalID')}
                        >
                            <Input prefix={<IdcardOutlined />} placeholder={t('nationalID')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="phone"
                            label={t('phone')}
                        >
                            <Input prefix={<PhoneOutlined />} placeholder={t('phone')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="address"
                            label={t('address')}
                        >
                            <Input prefix={<HomeOutlined />} placeholder={t('address')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="currenteducationlevel"
                            label={t('currentEducationLevel')}
                        >
                            <Select
                                placeholder={t('currentEducationLevel')}
                                options={educationOptions}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="schoolname"
                            label={t('schoolName')}
                        >
                            <Input prefix={<BankOutlined />} placeholder={t('schoolName')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="guardianname"
                            label={t('guardianName')}
                        >
                            <Input prefix={<TeamOutlined />} placeholder={t('guardianName')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="guardiancontact"
                            label={t('guardianContact')}
                        >
                            <Input prefix={<PhoneOutlined />} placeholder={t('guardianContact')} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default withRoleAccess(EditStudentModal, ['admin', 'superadmin']);